import { useMemo } from 'react';
import { PlusIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { DrawerItem } from './DrawerItem';
import { BikesIcon } from './BikesIcon';
import { programmeLabel, totalPoints } from '../../domain/workspace';
import { programLimits } from '../../domain/rules';
import { MAX_EXERCISES } from '../../domain/constants';
import { catalogueKeyFor, resolveProgramForType } from '../../data/catalogue';
import { useCatalogues } from '../hooks/useCatalogues';
import { deNumber } from '../../lib/format';
import { t } from '../../i18n';
import { ageTierLabel, starterTypeLabel } from '../../i18n/labels';
import type { Catalogue, CatalogueKey, Programme } from '../../types';

/** "{figures}/{limit} · {points}" — the starter type moved to the leading
 *  bicycle icon (`BikesIcon`), so it's not spelled out twice. Points show as
 *  "…" until the programme's catalogue has finished loading. */
function programmeMetaText(p: Programme, catalogues: ReadonlyMap<CatalogueKey, Catalogue>): string {
  const limit = programLimits(p)?.maxTotal ?? MAX_EXERCISES;
  const cat = catalogues.get(catalogueKeyFor(p.type));
  const points = cat ? deNumber(totalPoints(resolveProgramForType(catalogues, p.type, p.program))) : '…';
  return t('programme.list.meta', { count: String(p.program.length), limit: String(limit), points });
}

/** The "+ Neues Programm" row plus one row per existing programme, shared by
 *  the mobile nav drawer's Programm group and the desktop programme rail. */
export function ProgrammeList({
  programs,
  activeId,
  onSelect,
  onNew,
}: {
  programs: readonly Programme[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
}) {
  const keys = useMemo(() => programs.map((p) => catalogueKeyFor(p.type)), [programs]);
  const catalogues = useCatalogues(keys);

  return (
    <>
      <DrawerItem icon={<PlusIcon className="size-5 shrink-0" />} label={t('programme.new.button')} isActive={false} onClick={onNew} />
      {programs.length > 0 ? (
        <>
          <Separator className="my-1.5" />
          {programs.map((p) => (
            <DrawerItem
              key={p.id}
              icon={<BikesIcon type={p.type} />}
              label={programmeLabel(p, starterTypeLabel(p.type), ageTierLabel(p.ageTier))}
              meta={programmeMetaText(p, catalogues)}
              isActive={p.id === activeId}
              onClick={() => onSelect(p.id)}
            />
          ))}
        </>
      ) : null}
    </>
  );
}
